import PropTypes from 'prop-types';
import React, {Component} from 'react';
import throttle from 'lodash.throttle';

class ScrollHandler extends Component {
    static propTypes = {
        /** Function to call on scroll or on resize with true/false if the component is in view or not**/
        onLazyLoad: PropTypes.func,
        /** How many pixels from window bounds should this Lazy Loader unveil itself */
        threshold: PropTypes.number,
        /** Throttle setting in millisconds */
        throttleAmount: PropTypes.number,
        /** Boolean to use window as scroll parent */
        useWindowAsScrollParent: PropTypes.bool,
    };

    static defaultProps = {
        onLazyLoad: () => {},
        threshold: 0,
        throttleAmount: 100
    };

    constructor(props) {
        super(props);

        this.throttledHandler = throttle(this.lazyLoadHandler, props.throttleAmount);
    }

    componentDidMount() {
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.throttledHandler);
        }
        this.elem = this.el; // grab the reference constructed in render

        // Determine what element (or window) scrolls
        this.scrollParent = this.props.useWindowAsScrollParent
            ? window
            : this.getScrollParent(this.elem);
        if (this.elem === this.scrollParent) {
            this.elem = this.elem.firstChild;
        }
        this.scrollParent.addEventListener('scroll', this.throttledHandler);

        this.lazyLoadHandler();
    }

    componentWillUnmount() {
        this.detachListeners();
    }

    /*
        Unveil evaluates exposure on scroll events.  Since the Unveiling elements
        will not always be on the window, we need to find the container that is handling
        the scroll event, and hook up to that.

        This function walks up the tree and finds the containing element that
        should be handling that event.
    */
    getScrollParent(element) {
        let style = window.getComputedStyle(element);
        const excludeStaticParent = style.position === 'absolute';
        const overflowRegex = /(auto|scroll)/;

        if (style.position === 'fixed') {
            return window;
        }

        while (element) {
            style = window.getComputedStyle(element);
            if (excludeStaticParent && style.position === 'static') {
                continue;
            }
            if (overflowRegex.test(style.overflow + style.overflowY)) {
                return element;
            }
            element = element.parentElement;
        }

        return window;
    }

    detachListeners = () => {
        this.throttledHandler.cancel();
        this.scrollParent.removeEventListener('scroll', this.throttledHandler);
        window.removeEventListener('resize', this.throttledHandler);
    }

    lazyLoadHandler = () => {
        if (this.el && typeof this.el.getBoundingClientRect === 'function') {
            const windowTop = document.body.scrollTop;
            const windowBounds = windowTop + window.innerHeight;
            const elementTop = this.el.getBoundingClientRect().top + windowTop;
            const elementBounds = elementTop + this.el.getBoundingClientRect().height;
            const inView = elementBounds >= windowTop - this.props.threshold && elementTop <= windowBounds + this.props.threshold;

            this.props.onLazyLoad(inView);
        }
    }

    render() {
        const {children, onLazyLoad, threshold, throttleAmount, useWindowAsScrollParent, ...rest} = this.props; // eslint-disable-line no-unused-vars

        return (
            <div
                ref={
                    /* istanbul ignore next */
                    (c) => {
                        this.el = c;
                    }
                }
                {...rest}
            >
                {children}
            </div>
        );
    }
}

export default ScrollHandler;
